import React, { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Check, ChevronsUpDown, Loader2, CalendarCheck2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import api from "@/lib/api";
import { EVENT_TYPES, TYPE_ORDER, PRIORITIES, STATUSES, REMINDERS, getMeta } from "./calendarTypes";
import { toLocalInput } from "./calendarHelpers";
import { translateFr as tfr } from "@/i18n";

const EMPTY = {
  title: "",
  type: "meeting",
  starts_at: "",
  ends_at: "",
  all_day: false,
  location: "",
  description: "",
  player_id: "",
  club_id: "",
  priority: "normal",
  status: "planned",
  reminder: "none",
  participants: "",
};

const FIELD_LABEL = "text-[13px] font-medium text-foreground";
const CONTROL = "mt-1.5 h-11";

/* Searchable single-select (athlete / club) built on Popover + Command. */
function SearchSelect({ id, value, onChange, items, placeholder, searchPlaceholder, emptyLabel, noneLabel, testid }) {
  const [open, setOpen] = useState(false);
  const selected = items.find((i) => i.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className={cn(
            "flex w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm",
            "hover:border-foreground/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors",
            CONTROL, !selected && "text-muted-foreground"
          )}
        >
          <span className="truncate">{selected?.name || placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground/70" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testid}-search`} />
          <CommandList className="max-h-[220px]">
            <CommandEmpty>{items.length === 0 ? emptyLabel: tfr("Aucun résultat")}</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__none__" onSelect={() => { onChange(""); setOpen(false); }} data-testid={`${testid}-none`}>
                <Check className={cn("mr-2 h-3.5 w-3.5", value ? "opacity-0" : "opacity-100")} />
                {noneLabel}
              </CommandItem>
              {items.map((i) => (
                <CommandItem
                  key={i.id}
                  value={`${i.name} ${i.club || ""} ${i.sport || ""}`}
                  onSelect={() => { onChange(i.id); setOpen(false); }}
                  data-testid={`${testid}-option-${i.id}`}
                >
                  <Check className={cn("mr-2 h-3.5 w-3.5", value === i.id ? "opacity-100" : "opacity-0")} />
                  <span className="min-w-0">
                    <span className="block truncate">{i.name}</span>
                    {(i.club || i.sport) && (
                      <span className="block text-[11px] text-muted-foreground truncate">{[i.club, i.sport].filter(Boolean).join(" · ")}</span>
                    )}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/* Participants as removable chips over the existing string field (comma-separated). */
function ParticipantsInput({ value, onChange, suggestions }) {
  const [text, setText] = useState("");
  const chips = useMemo(() => (value || "").split(",").map((s) => s.trim()).filter(Boolean), [value]);
  const add = (name) => {
    const n = name.trim();
    if (!n || chips.some((c) => c.toLowerCase() === n.toLowerCase())) { setText(""); return; }
    onChange([...chips, n].join(", "));
    setText("");
  };
  const remove = (name) => onChange(chips.filter((c) => c !== name).join(", "));
  const matches = text.trim()
    ? suggestions.filter((s) => s.toLowerCase().includes(text.toLowerCase()) && !chips.some((c) => c.toLowerCase() === s.toLowerCase())).slice(0, 5)
    : [];
  const commit = () => add(matches.length ? matches[0] : text);
  return (
    <div>
      <div
        className={cn(
          "mt-1.5 flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 min-h-[44px]",
          "focus-within:ring-2 focus-within:ring-ring transition-shadow"
        )}
        data-testid="event-editor-participants-box"
      >
        {chips.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 rounded-full bg-secondary text-foreground text-xs font-medium pl-2.5 pr-1 py-1" data-testid={`participant-chip-${c}`}>
            {c}
            <button type="button" onClick={() => remove(c)} aria-label={`Retirer ${c}`}
              className="rounded-full p-0.5 hover:bg-foreground/10 transition-colors" data-testid={`participant-remove-${c}`}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            else if (e.key === "Backspace" && !text && chips.length) remove(chips[chips.length - 1]);
          }}
          onBlur={() => text.trim() && add(text)}
          placeholder={chips.length ? "" : tfr("Ajouter des participants")}
          aria-label={tfr("Ajouter des participants")}
          className="flex-1 min-w-[120px] h-7 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          data-testid="event-editor-participants"
        />
      </div>
      {matches.length > 0 && (
        <div className="mt-1 rounded-md border border-border bg-popover shadow-md py-1" data-testid="participants-suggestions">
          {matches.map((m) => (
            <button key={m} type="button" onMouseDown={(e) => { e.preventDefault(); add(m); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent transition-colors" data-testid={`participants-suggestion-${m}`}>
              {m}
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground mt-1">{tfr("Entrée pour ajouter · plusieurs participants possibles")}</p>
    </div>
  );
}

const dPart = (v) => (v || "").slice(0, 10);
const tPart = (v) => (v || "").slice(11, 16);

/**
 * EventEditor — create / edit dialog used everywhere in the Calendar page.
 * Responsive: mobile bottom sheet, desktop centered modal. Theme-token based (light + dark).
 */
export default function EventEditor({ open, onOpenChange, defaultDate, event, onSaved }) {
  const isEdit = !!event?.id;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [players, setPlayers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [errors, setErrors] = useState({});
  const [confirmClose, setConfirmClose] = useState(false);
  const initialRef = useRef("");

  useEffect(() => {
    if (!open) { setConfirmClose(false); setErrors({}); return; }
    api.get("/players").then((r) => setPlayers(r.data || [])).catch(() => {});
    api.get("/clubs").then((r) => setClubs(r.data || [])).catch(() => {});
    let next;
    if (isEdit) {
      next = {
        title: event.title || "",
        type: event.type || "meeting",
        starts_at: event.starts_at ? toLocalInput(event.starts_at) : "",
        ends_at: event.ends_at ? toLocalInput(event.ends_at) : "",
        all_day: !!event.all_day,
        location: event.location || "",
        description: event.description || event.notes || "",
        player_id: event.player_id || "",
        club_id: event.club_id || "",
        priority: event.priority || "normal",
        status: event.status || "planned",
        reminder: event.reminder || "none",
        participants: event.participants || "",
      };
    } else {
      const d = defaultDate ? new Date(defaultDate) : new Date();
      if (!d.getHours() && !d.getMinutes()) d.setHours(10, 0, 0, 0);
      const end = new Date(d.getTime() + 60 * 60 * 1000);
      next = { ...EMPTY, starts_at: toLocalInput(d), ends_at: toLocalInput(end) };
    }
    setForm(next);
    initialRef.current = JSON.stringify(next);
  }, [open, isEdit, event, defaultDate]);

  const meta = useMemo(() => getMeta(form.type), [form.type]);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k === "ends_at" || k === "starts_at" ? "dates" : k]: null })); };

  const dirty = JSON.stringify(form) !== initialRef.current;
  const handleOpenChange = (v) => {
    if (!v && dirty && !saving) { setConfirmClose(true); return; }
    onOpenChange(v);
  };

  const selectedPlayer = players.find((p) => p.id === form.player_id);
  const suggestedClub = selectedPlayer?.club
    ? clubs.find((c) => c.name?.toLowerCase() === selectedPlayer.club.toLowerCase() && c.id !== form.club_id)
    : null;

  const setDate = (key, date) => {
    const t = tPart(form[key]) || (key === "starts_at" ? "10:00" : "11:00");
    set(key, date ? `${date}T${form.all_day ? (key === "starts_at" ? "00:00" : "23:59") : t}` : "");
  };
  const setTime = (key, time) => {
    const d = dPart(form[key]) || dPart(form.starts_at) || new Date().toISOString().slice(0, 10);
    set(key, `${d}T${time || "00:00"}`);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const errs = {};
    if (!form.title.trim()) errs.title = tfr("Le titre est requis.");
    if (!form.starts_at) errs.dates = tfr("La date de début est requise.");
    else if (form.ends_at && new Date(form.ends_at) < new Date(form.starts_at)) errs.dates = tfr("La fin doit être postérieure au début.");
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        type: form.type,
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        all_day: !!form.all_day,
        location: form.location.trim(),
        description: form.description.trim(),
        player_id: form.player_id || null,
        club_id: form.club_id || null,
        priority: form.priority,
        status: form.status,
        reminder: form.reminder,
        participants: form.participants.trim(),
      };
      let res;
      if (isEdit) res = await api.patch(`/events/${event.id}`, payload);
      else res = await api.post("/events", payload);
      toast.success(isEdit ? tfr("Événement modifié") : tfr("Événement créé"));
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : tfr("Impossible de créer l'événement. Réessayez."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/55 backdrop-blur-sm",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DialogPrimitive.Content
          data-testid="event-editor"
          aria-describedby={undefined}
          className={cn(
            "fixed z-50 bg-card text-card-foreground border border-border shadow-xl flex flex-col overflow-hidden",
            "left-0 right-0 bottom-0 h-[92vh] max-h-[92vh] rounded-t-3xl",
            "md:left-1/2 md:right-auto md:bottom-auto md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2",
            "md:w-[95vw] md:max-w-3xl md:h-auto md:max-h-[92vh] md:rounded-2xl",
            "duration-300 ease-out",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            "md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95"
          )}
        >
          {/* Mobile drag handle */}
          <div className="md:hidden flex justify-center pt-2 pb-1">
            <div className="h-1 w-10 rounded-full bg-muted-foreground/25" aria-hidden />
          </div>

          {/* Header */}
          <header className="flex items-center justify-between px-5 md:px-6 pt-3 md:pt-5 pb-4 border-b border-border">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="h-9 w-9 rounded-xl bg-athlynx/10 border border-athlynx/20 text-athlynx flex items-center justify-center shrink-0">
                <meta.Icon className="h-4 w-4" aria-hidden />
              </div>
              <DialogPrimitive.Title className="text-base md:text-lg font-semibold tracking-tight truncate">
                {isEdit ? tfr("Modifier l'événement") : tfr("Nouvel événement")}
              </DialogPrimitive.Title>
            </div>
            <DialogPrimitive.Close
              className="rounded-full h-9 w-9 flex items-center justify-center border border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors shrink-0"
              aria-label={tfr("Fermer")}
              data-testid="event-editor-close"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </header>

          {/* Body */}
          <form onSubmit={submit} className="flex-1 min-h-0 overflow-y-auto px-5 md:px-6 py-5 space-y-5">
            {/* 1. Title */}
            <div>
              <Label htmlFor="ev-title" className={FIELD_LABEL}>{tfr("Titre")} <span className="text-athlynx" aria-hidden>*</span></Label>
              <Input
                id="ev-title"
                autoFocus
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder={tfr("Nom de l'événement")}
                data-testid="event-editor-title"
                aria-invalid={!!errors.title}
                aria-describedby={errors.title ? "ev-title-error" : undefined}
                className={cn(CONTROL, errors.title && "border-athlynx focus-visible:ring-athlynx/40")}
              />
              {errors.title && <p id="ev-title-error" className="text-xs text-athlynx mt-1" role="alert" data-testid="event-editor-title-error">{errors.title}</p>}
            </div>

            {/* 2-3. Type + Priority */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className={FIELD_LABEL}>Type <span className="text-athlynx" aria-hidden>*</span></Label>
                <Select value={form.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger data-testid="event-editor-type" className={CONTROL}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPE_ORDER.map((k) => {
                      const m = EVENT_TYPES[k];
                      const Icon = m.Icon;
                      return (
                        <SelectItem key={k} value={k}>
                          <span className="flex items-center gap-2">
                            <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden /> {m.label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={FIELD_LABEL}>{tfr("Priorité")}</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger data-testid="event-editor-priority" className={CONTROL}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* 4. All day */}
            <div className="flex items-center justify-between rounded-xl border border-border bg-secondary/40 px-3.5 py-3">
              <div>
                <Label htmlFor="ev-allday" className="cursor-pointer text-sm font-medium">{tfr("Toute la journée")}</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">{tfr("Masque les heures et conserve uniquement la date.")}</p>
              </div>
              <Switch
                id="ev-allday"
                checked={form.all_day}
                onCheckedChange={(v) => set("all_day", v)}
                data-testid="event-editor-allday"
              />
            </div>

            {/* 5. Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className={FIELD_LABEL}>{tfr("Début")} <span className="text-athlynx" aria-hidden>*</span></Label>
                <div className={cn("grid gap-2 mt-1.5", form.all_day ? "grid-cols-1" : "grid-cols-[1.2fr_1fr]")}>
                  <Input type="date" value={dPart(form.starts_at)} onChange={(e) => setDate("starts_at", e.target.value)}
                    aria-label={tfr("Date de début")} data-testid="event-editor-start" className="h-11 mt-0" />
                  {!form.all_day && (
                    <Input type="time" value={tPart(form.starts_at)} onChange={(e) => setTime("starts_at", e.target.value)}
                      aria-label={tfr("Heure de début")} data-testid="event-editor-start-time" className="h-11 mt-0" />
                  )}
                </div>
              </div>
              <div>
                <Label className={FIELD_LABEL}>Fin</Label>
                <div className={cn("grid gap-2 mt-1.5", form.all_day ? "grid-cols-1" : "grid-cols-[1.2fr_1fr]")}>
                  <Input type="date" value={dPart(form.ends_at)} onChange={(e) => setDate("ends_at", e.target.value)}
                    aria-label={tfr("Date de fin")} data-testid="event-editor-end" aria-invalid={!!errors.dates} className="h-11 mt-0" />
                  {!form.all_day && (
                    <Input type="time" value={tPart(form.ends_at)} onChange={(e) => setTime("ends_at", e.target.value)}
                      aria-label={tfr("Heure de fin")} data-testid="event-editor-end-time" className="h-11 mt-0" />
                  )}
                </div>
              </div>
            </div>
            {errors.dates && <p className="text-xs text-athlynx -mt-2" role="alert" data-testid="event-editor-dates-error">{errors.dates}</p>}

            {/* 6. Location */}
            <div>
              <Label htmlFor="ev-location" className={FIELD_LABEL}>{tfr("Lieu")}</Label>
              <Input
                id="ev-location"
                value={form.location}
                onChange={(e) => set("location", e.target.value)}
                placeholder={tfr("Ex. Stade, bureau, visioconférence…")}
                data-testid="event-editor-location"
                className={CONTROL}
              />
            </div>

            {/* 7-8. Athlete + Club */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ev-player" className={FIELD_LABEL}>{tfr("Athlète lié")}</Label>
                <SearchSelect
                  id="ev-player"
                  value={form.player_id}
                  onChange={(v) => set("player_id", v)}
                  items={players}
                  placeholder={tfr("Sélectionner un athlète")}
                  searchPlaceholder={tfr("Rechercher un athlète…")}
                  emptyLabel={tfr("Aucun athlète disponible")}
                  noneLabel={tfr("Aucun athlète")}
                  testid="event-editor-player"
                />
              </div>
              <div>
                <Label htmlFor="ev-club" className={FIELD_LABEL}>{tfr("Club lié")}</Label>
                <SearchSelect
                  id="ev-club"
                  value={form.club_id}
                  onChange={(v) => set("club_id", v)}
                  items={clubs}
                  placeholder={tfr("Sélectionner un club")}
                  searchPlaceholder={tfr("Rechercher un club…")}
                  emptyLabel={tfr("Aucun club disponible")}
                  noneLabel={tfr("Aucun club")}
                  testid="event-editor-club"
                />
                {suggestedClub && (
                  <button type="button" onClick={() => set("club_id", suggestedClub.id)}
                    className="text-[11px] text-athlynx hover:underline mt-1 inline-flex items-center gap-1"
                    data-testid="event-editor-club-suggestion">
                    Utiliser le club actuel ({suggestedClub.name})
                  </button>
                )}
              </div>
            </div>

            {/* 9. Participants */}
            <div>
              <Label className={FIELD_LABEL}>Participants</Label>
              <ParticipantsInput
                value={form.participants}
                onChange={(v) => set("participants", v)}
                suggestions={players.map((p) => p.name)}
              />
            </div>

            {/* 10-11. Reminder + Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className={FIELD_LABEL}>{tfr("Rappel")}</Label>
                <Select value={form.reminder} onValueChange={(v) => set("reminder", v)}>
                  <SelectTrigger data-testid="event-editor-reminder" className={CONTROL}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REMINDERS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.value === "none" ? tfr("Aucun rappel") : r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={FIELD_LABEL}>{tfr("Statut")}</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="event-editor-status" className={CONTROL}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* 12. Description */}
            <div>
              <Label htmlFor="ev-description" className={FIELD_LABEL}>Description</Label>
              <Textarea
                id="ev-description"
                rows={3}
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder={tfr("Ajouter une description ou des notes…")}
                data-testid="event-editor-description"
                className="mt-1.5 resize-none"
              />
            </div>
          </form>

          {/* Footer */}
          <footer className="flex items-center justify-end gap-2 px-5 md:px-6 py-4 border-t border-border bg-card pb-[calc(env(safe-area-inset-bottom)+16px)] md:pb-4">
            <Button
              type="button"
              variant="ghost"
              className="h-11 px-4"
              onClick={() => handleOpenChange(false)}
              data-testid="event-editor-cancel"
            >
              {tfr("Annuler")}
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={saving}
              className="h-11 px-5 gap-1.5 bg-athlynx text-white hover:bg-athlynx/90"
              data-testid="event-editor-submit"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {isEdit ? tfr("Enregistrement…") : tfr("Création…")}</>
              ) : (
                <><CalendarCheck2 className="h-4 w-4" aria-hidden /> {isEdit ? tfr("Enregistrer") : tfr("Créer l'événement")}</>
              )}
            </Button>
          </footer>

          {/* Unsaved-changes confirmation */}
          {confirmClose && (
            <div className="absolute inset-0 z-10 bg-background/70 backdrop-blur-[2px] flex items-center justify-center p-6" role="alertdialog" aria-label={tfr("Quitter sans enregistrer ?")} data-testid="event-editor-discard-confirm">
              <div className="rounded-2xl border border-border bg-card shadow-xl p-5 w-full max-w-sm">
                <p className="text-sm font-semibold">{tfr("Quitter sans enregistrer ?")}</p>
                <p className="text-xs text-muted-foreground mt-1">{tfr("Les modifications de cet événement seront perdues.")}</p>
                <div className="flex justify-end gap-2 mt-4">
                  <Button type="button" variant="ghost" size="sm" className="h-10" onClick={() => setConfirmClose(false)} data-testid="event-editor-discard-cancel">
                    Continuer la modification
                  </Button>
                  <Button type="button" size="sm" className="h-10 bg-athlynx text-white hover:bg-athlynx/90"
                    onClick={() => { setConfirmClose(false); onOpenChange(false); }} data-testid="event-editor-discard-confirm-btn">
                    Quitter
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
